// @ts-check
/* eslint-disable max-lines */

import theme from '@gaia/theme';
import {StyledEngineProvider, ThemeProvider} from '@mui/material/styles';
import {Component} from 'react';

import ConfirmationDialog from './ConfirmationDialog';
import Dropdown from './Dropdown';
import MoveDialog from './MoveDialog';
import Navigator from './Navigator';

import {
    addObjects,
    addSelection,
    removeObjects,
    removeSelection,
    setDropdownMetadata,
    setEditing,
    setMoveDialogOpen,
    setName,
    setObjects,
    setPath,
    setPermissions,
    setRemoveDialogOpen,
    setSearch,
    setSelection,
    setSync,
    updateChildren,
} from '@gaia/stores/folderui-actions';
import Store from '@gaia/stores/folderui-store';

/**
 * @typedef FolderUIProps
 * @prop {'browse' | 'choose'} mode
 * @prop {Record<string, any>} [objects]
 * @prop {string[]} path
 * @prop {boolean} own
 * @prop {string} toplevel
 * @prop {Record<string, any>} [topFolders]
 * @prop {string} limit
 * @prop {(id: string, callback: (objects: Record<string, any>, children: string[]) => void) => void} fetchChildren
 *
 * Limited to mode='browse'
 * @prop {(id: string, type: string, path: string[], isTop: boolean) => void} [onNavigate]
 * @prop {(id: string, type: string, name: string) => void} [onRename]
 * @prop {(id: string, type: string, destId: string) => void} [onMove]
 * @prop {(id: string, type: string) => void} [onRemove]
 * @prop {(id: string, type: string, sync: boolean) => void} [onSync]
 * @prop {(id: string, type: string, permissions: boolean) => void} [onPublic]
 *
 * Limited to mode='choose'
 * @prop {(id: string, type: string, target?: string) => void} [onChoose]
 */

/** @extends Component<FolderUIProps> */
class FolderUI extends Component {
    /** @param {FolderUIProps} props */
    constructor(props) {
        super(props);

        this.store = new Store().store;

        // Load initial objects and path
        this.store.dispatch(setObjects(props.objects));
        this.store.dispatch(setPath(props.path));

        this.state = this.store.getState();

        this.unsubscribe = this.store.subscribe(() => this.update());

        // Event listener for forward and backward navigation.
        if (props.mode === 'browse') {
            window.addEventListener('popstate', (e) => {
                if (e.state && e.state.path) {
                    this.store.dispatch(setPath(e.state.path));
                }
            });
        }
    }

    componentWillUnmount() {
        this.unsubscribe();
    }

    update() {
        this.setState(this.store.getState());
    }

    /**
     * Handler for pathChange events, either up or down. Fetches children if needed.
     *
     * @param  {string[]} path - Array of ids.
     * @param  {string} id - Displayed id.
     */
    onPathChange(path, id) {
        this.store.dispatch(setPath(path));

        if (this.props.fetchChildren) {
            this.props.fetchChildren(
                id,
                (/** @type {any} */ objects, /** @type {any} */ children) => {
                    setTimeout(() => {
                        this.store.dispatch(addObjects(id, objects, children));
                    }, 550);
                }
            );
        }
    }

    /**
     * Handler for saveName event. Turns off editing and make API call to rename.
     * @param {string} id
     * @param {string} type
     * @param {string} name
     */
    onSaveName(id, type, name) {
        this.store.dispatch(setEditing(false));

        this.props.onRename?.(id, type, name);
    }

    /**
     * Show file menu with metadata, for 1 or more objects.
     *
     * @param  {import("react").MouseEvent<HTMLButtonElement>} e - Event that triggered the menu.
     * @param  {string} [id] - Object id if a single object was selected.
     */
    onFileMenu(e, id) {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();

        this.store.dispatch(
            setDropdownMetadata({
                open: true,
                // @ts-ignore
                element: e.target,
            })
        );

        let sync = true;
        let permissions = true;

        // If handler was triggered from a single object, update the selection.
        if (id) {
            this.store.dispatch(setSelection([id]));

            sync = this.state.objects[id].sync;
            permissions = this.state.objects[id].public;
        } else {
            for (const selectionId of this.state.selection) {
                sync = sync && this.state.objects[selectionId].sync;
                permissions = permissions && this.state.objects[selectionId].public;
            }
        }

        this.store.dispatch(
            setDropdownMetadata({
                sync,
                public: permissions,
                count: this.state.selection.size,
            })
        );
    }

    /**
     * Start editing selected objects.
     */
    onRename() {
        this.onCloseFileMenu();

        this.store.dispatch(setEditing(true));
    }

    /**
     * Set sync state of selected objects.
     * @param {boolean} sync - Sync state.
     */
    onSync(sync) {
        this.store.dispatch(setDropdownMetadata({sync}));

        this.store.dispatch(setSync(sync));

        for (const id of this.state.selection) {
            this.props.onSync?.(id, this.state.objects[id].type, sync);
        }
    }

    /**
     * Set public/private state of object.
     *
     * @param {boolean} permissions - Public = true, private = false.
     */
    onPublic(permissions) {
        this.store.dispatch(
            setDropdownMetadata({
                public: permissions,
            })
        );

        this.store.dispatch(setPermissions(permissions));

        for (const id of this.state.selection) {
            this.props.onPublic?.(id, this.state.objects[id].type, permissions);
        }
    }

    /**
     * Set selection to array of object ids.
     *
     * @param {string[]} ids
     */
    onSetSelection(ids) {
        this.store.dispatch(setSelection(ids));
    }

    /**
     * Add array of object ids to selection.
     *
     * @param {string[]} ids
     */
    onAddSelection(ids) {
        this.store.dispatch(addSelection(ids));
    }

    /**
     * Remove array of object ids from selection.
     *
     * @param {string[]} ids
     */
    onRemoveSelection(ids) {
        this.store.dispatch(removeSelection(ids));
    }

    /**
     * Search for string.
     *
     * @param {string} searchString
     */
    onSearch(searchString) {
        this.store.dispatch(setSearch(searchString));
    }

    /**
     * Change name of an object.
     *
     * @param {string} id object id being edited.
     * @param {string} name new name.
     */
    onEdit(id, name) {
        this.store.dispatch(setName(id, name));
    }

    /**
     * Cancel editing.
     */
    onCancelName() {
        this.store.dispatch(setEditing(false));
    }

    /**
     * Close file menu.
     */
    onCloseFileMenu() {
        setTimeout(() => {
            try {
                this.store.dispatch(setDropdownMetadata({open: false}));
            } catch (e) {
                // Handle this async error
                // console.log(e)
            }
        }, 1);
    }

    /**
     * Open the MoveDialog
     */
    onMove() {
        this.onCloseFileMenu();
        this.store.dispatch(setMoveDialogOpen({open: true}));
    }

    /**
     * Open the RemoveDialog
     */
    onRemove() {
        this.onCloseFileMenu();
        this.store.dispatch(setRemoveDialogOpen({open: true}));
    }

    /**
     * Close the movingDialog
     */
    onCloseMovingDialog() {
        this.store.dispatch(setMoveDialogOpen({open: false}));
    }

    /**
     * Close the confirmationDialog
     */
    onCloseConfirmationDialog() {
        this.store.dispatch(setRemoveDialogOpen({open: false}));
    }

    /**
     * Handler for onChoose event. Make API call to move and update
     * children list on both origin and destination folder
     * (destId, destType) is the target
     * @param {string} destID
     * @param {string} destType
     */
    onChoose(destID, destType) {
        this.store.dispatch(setMoveDialogOpen({open: false}));

        for (const id of this.state.selection) {
            const type = this.state.objects[id].type;
            // An item is moved only in a folder and not itself
            if (destType === 'folder' && destID !== id) {
                // API call - function defined in gaia-side
                this.props.onMove?.(id, type, destID);
            }
        }
        // Update the frontend
        const parentID = this.state.path[this.state.path.length - 1];
        if (parentID) {
            // Update children
            this.store.dispatch(updateChildren(parentID, destID, destType, this.props.topFolders));
            // Removing from the selection
            this.store.dispatch(setSelection([]));
        }
    }

    /**
     * Handler for onDelete event. Remove the object from the store
     * and make API call to remove.
     */
    onDelete() {
        this.store.dispatch(setRemoveDialogOpen({open: false}));

        for (const id of this.state.selection) {
            const type = this.state.objects[id].type;
            this.props.onRemove?.(id, type); // API call
        }
        const parentID = this.state.path[this.state.path.length - 1];
        this.store.dispatch(removeObjects(parentID)); // Removing from the store
        this.store.dispatch(setSelection([])); // Removing from the selection
    }

    render() {
        return (
            <div className='folderui'>
                <Navigator
                    mode={this.props.mode}
                    objects={this.state.objects}
                    path={this.state.path}
                    own={this.props.own}
                    selection={this.state.selection}
                    search={this.state.search.string}
                    toplevel={this.props.toplevel}
                    limit={this.props.limit}
                    onPathChange={(path, id) => this.onPathChange(path, id)}
                    onNavigate={this.props.onNavigate}
                    onEdit={(id, name) => this.onEdit(id, name)}
                    onSaveName={(id, type, name) => this.onSaveName(id, type, name)}
                    onCancelName={() => this.onCancelName()}
                    onSetSelection={(ids) => this.onSetSelection(ids)}
                    onAddSelection={(ids) => this.onAddSelection(ids)}
                    onRemoveSelection={(ids) => this.onRemoveSelection(ids)}
                    onFileMenu={(e, id) => this.onFileMenu(e, id)}
                    onSearch={(str) => this.onSearch(str)}
                    onChoose={this.props.onChoose}
                />
                {this.props.mode === 'browse' && (
                    <div>
                        <Dropdown
                            open={this.state.dropdown.open}
                            anchorEl={this.state.dropdown.element}
                            sync={this.state.dropdown.sync}
                            public={this.state.dropdown.public}
                            count={this.state.selection.size}
                            onRename={() => this.onRename()}
                            onMove={() => this.onMove()}
                            onRemove={() => this.onRemove()}
                            onSync={(sync) => this.onSync(sync)}
                            onPublic={(permissions) => this.onPublic(permissions)}
                            onClose={() => this.onCloseFileMenu()}
                        />
                        <MoveDialog
                            path={[]} // to display from the root level
                            open={this.state.moveDialog.open}
                            onClose={() => this.onCloseMovingDialog()}
                            onChoose={(id, type) => this.onChoose(id, type)}
                            objects={this.props.topFolders}
                            fetchChildren={this.props.fetchChildren}
                        />
                        <StyledEngineProvider injectFirst>
                            <ThemeProvider theme={theme}>
                                <ConfirmationDialog
                                    open={this.state.confirmationDialog.open}
                                    onRemove={() => this.onDelete()}
                                    onClose={() => this.onCloseConfirmationDialog()}
                                />
                            </ThemeProvider>
                        </StyledEngineProvider>
                    </div>
                )}
            </div>
        );
    }
}

export default FolderUI;
